import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { QueryOptions } from '@/types';

import {
    quoteQueries,
    QuoteEquityQueryParams,
    QuoteSearchQueryParams,
    QuoteBondsSearchQueryParams,
    QuoteMutualFundDetailsQueryParams,
    QuoteMutualFundCheckQueryParams,
    TradingOptionChainDatesQueryParams,
    TradingOptionChainQuotesQueryParams,
} from './queries';
import { OptionChain, OptionChainDates, Quote, TimeRange, divide } from '@orbis-systems/orbis-api-middleware';
import { getBondQuoteCountry, getBondSearchYtw, getQuoteEquityQueryDefinition } from './utils';
import { BondQuoteDetail, BondSearchResult } from './types';
import { STALE_TIME_10_MIN } from '../constants';
import { getQueryStates } from '../utils';
import { toast } from 'sonner';

export type UseGetQuoteEquityQueryParams = QuoteEquityQueryParams & {
    enabled?: boolean;
    staleTime?: number;
};

export const useGetQuoteEquityQuery = (params: NonNullable<UseGetQuoteEquityQueryParams>) => {
    const { enabled = true, staleTime, ...queryParams } = params;

    const queryDefinition = getQuoteEquityQueryDefinition(queryParams);

    return useQuery({ ...queryDefinition, enabled, staleTime });
};

export type UseGetQuoteEquityHistoricalQueryParams = {
    symbol: string;
    range: TimeRange;
    enabled?: boolean;
};

export const useGetQuoteEquityHistoricalQuery = (params: NonNullable<UseGetQuoteEquityHistoricalQueryParams>) => {
    const { enabled = true, range, symbol } = params;

    const queryDefinition =
        range === TimeRange.OneDay
            ? quoteQueries.intradayChartData({ symbol })
            : quoteQueries.historical({ symbol, range });

    return useQuery({ ...queryDefinition, enabled });
};

export type UseQuoteSearchQueryParams = QuoteSearchQueryParams & {
    enabled?: boolean;
    staleTime?: number;
};

export const useQuoteSearchQuery = (params: NonNullable<UseQuoteSearchQueryParams>) => {
    const { enabled = true, staleTime, ...queryParams } = params;

    const queryDefinition = quoteQueries.search(queryParams);

    return useQuery({ ...queryDefinition, enabled, staleTime });
};

export type UseQuoteBondsSearchQueryParams = QuoteBondsSearchQueryParams & {
    enabled?: boolean;
    staleTime?: number;
};

export const useQuoteBondsSearchQuery = (params: NonNullable<UseQuoteBondsSearchQueryParams>) => {
    const { enabled = true, staleTime, ...queryParams } = params;

    const queryDefinition = quoteQueries.bondsSearch(queryParams);

    return useQuery({
        ...queryDefinition,
        enabled,
        staleTime,
        select: (data): BondSearchResult[] =>
            data.map((bond) => ({
                ...bond,
                lastPrice: divide(bond.lastPrice || 0, 100),
                country: getBondQuoteCountry(bond),
                ytw: getBondSearchYtw(bond),
            })),
    });
};

export type UseGetQuoteBondQueryParams = {
    symbols: string;
    enabled?: boolean;
    staleTime?: number;
};

export const useGetQuoteBondQuery = (params: UseGetQuoteBondQueryParams) => {
    const { enabled = true, staleTime, symbols } = params;

    const quoteQueryDefinition = quoteQueries.getBonds({ identifiers: symbols });
    const quoteQuery = useQuery({
        ...quoteQueryDefinition,
        enabled,
        staleTime,
        select: (quote): BondQuoteDetail[] => {
            if (!quote) return [];

            return [
                {
                    ...quote,
                    daycount: quote.daycount || quote.day_count || '',
                    closeYtw: quote.closeYtw ?? (quote.close_ytw != null ? Number(quote.close_ytw) : undefined),
                    country: getBondQuoteCountry(quote) || '',
                },
            ];
        },
    });

    return {
        data: quoteQuery.data ?? [],
        error: quoteQuery.error,
        ...getQueryStates([quoteQuery]),
        dataUpdatedAt: new Date(quoteQuery.dataUpdatedAt ?? 0),
        refetch: quoteQuery.refetch,
    };
};

export type UseGetAllowedMutualFundsQueryParams = {
    enabled?: boolean;
    staleTime?: number;
};

export const useGetAllowedMutualFundsQuery = (params: UseGetAllowedMutualFundsQueryParams = {}) => {
    const { enabled = true, staleTime } = params;

    const queryDefinition = quoteQueries.mutualFundsAllowed();

    return useQuery({ ...queryDefinition, enabled, staleTime });
};

export type UseGetMutualFundDetailsQueryParams = QuoteMutualFundDetailsQueryParams & {
    enabled?: boolean;
    staleTime?: number;
};

export const useGetMutualFundDetailsQuery = (params: NonNullable<UseGetMutualFundDetailsQueryParams>) => {
    const { enabled = true, staleTime, ...queryParams } = params;

    const queryDefinition = quoteQueries.mutualFundDetails(queryParams);

    return useQuery({ ...queryDefinition, enabled, staleTime });
};

export type UseCheckMutualFundQueryParams = QuoteMutualFundCheckQueryParams & {
    enabled?: boolean;
    staleTime?: number;
};

export const useCheckMutualFundQuery = (params: UseCheckMutualFundQueryParams) => {
    const { enabled = true, staleTime, symbol } = params;

    return useQuery({
        ...quoteQueries.mutualFundCheck({ symbol }),
        enabled,
        staleTime,
        select: (data) => Boolean(data[symbol]),
    });
};

export type UseGetQuoteOptionQueryParams = {
    symbols: string[];
    greeks?: boolean;
};

export const useGetQuoteOptionQuery = (params: UseGetQuoteOptionQueryParams, options: QueryOptions<Quote[]>) => {
    return useQuery({ ...quoteQueries.getOption(params), staleTime: STALE_TIME_10_MIN, ...options });
};

export const useGetOptionChainDatesQuery = (
    params: TradingOptionChainDatesQueryParams,
    options: QueryOptions<OptionChainDates>,
) => {
    const query = useQuery({ ...quoteQueries.optionChainDates(params), staleTime: STALE_TIME_10_MIN, ...options });
    useEffect(() => {
        if (query.error) toast.error('Failed to load option chain dates');
    }, [query.error]);

    return query;
};

export const useGetOptionChainQuotesQuery = (
    params: TradingOptionChainQuotesQueryParams,
    options: QueryOptions<OptionChain>,
) => {
    const query = useQuery({ ...quoteQueries.optionChainQuotes(params), staleTime: STALE_TIME_10_MIN, ...options });
    useEffect(() => {
        if (query.error) toast.error('Failed to load option chain quotes');
    }, [query.error]);
    return query;
};

export type UseGetQuoteOptionHistoricalQueryParams = {
    symbol: string;
    range: TimeRange;
    enabled?: boolean;
};

export const useGetQuoteOptionHistoricalQuery = (params: UseGetQuoteOptionHistoricalQueryParams) => {
    const { enabled = true, symbol, range } = params;

    const queryDefinition = quoteQueries.getOptionHistorical({ symbol, range });

    return useQuery({ ...queryDefinition, enabled });
};

export type UseGetQuotesFxRateQueryParams = {
    symbols: string[];
    enabled?: boolean;
    staleTime?: number;
    gcTime?: number;
};

export const useGetQuotesFxRateQuery = (params: UseGetQuotesFxRateQueryParams) => {
    const { symbols, enabled, staleTime, gcTime } = params;

    const queryDefinition = quoteQueries.fxRate({ symbols });

    return useQuery({
        ...queryDefinition,
        enabled,
        staleTime,
        gcTime,
    });
};

export type UseGetMidasQuotesQueryParams = {
    staleTime?: number;
    enabled?: boolean;
    symbols: string[];
};

export const useGetMidasEquityQuotesQuery = (params: UseGetMidasQuotesQueryParams) => {
    const { symbols, enabled, staleTime } = params;
    return useQuery({ ...quoteQueries.getMidasEquityQuotes(symbols), enabled, staleTime });
};

export const useGetMidasBondQuotesQuery = (params: UseGetMidasQuotesQueryParams) => {
    const { symbols, enabled, staleTime } = params;
    return useQuery({ ...quoteQueries.getMidasBondQuotes(symbols), enabled, staleTime });
};

export const useGetMidasOptionQuotesQuery = (params: UseGetMidasQuotesQueryParams) => {
    const { symbols, enabled, staleTime } = params;
    return useQuery({ ...quoteQueries.getMidasOptionQuotes(symbols), enabled, staleTime });
};
